import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { translations } from '@shippypro/translations';

import {
  PopoverBody,
  PopoverHeader,
  UncontrolledPopover,
} from '@shippypro/design-system-web';
import { useGetDeviceSize } from '@shippypro/design-system-web/hooks';

type OrderTagsProps = {
  tags: string[];
};

/**
 * New OrderTags component for new table design.
 * Preview OrderTagsBadge component was left as it is because used in other places.
 *
 * @interface IOrderTagsBadgeProps
 * @author Orkhan Rustamli <orkhan.rustamli@shippypro.com>
 */
const OrderTags = ({ tags }: OrderTagsProps) => {
  const { t } = useTranslation(),
    transColumns = translations.ship.table.columns;

  const { isMobile, isTablet } = useGetDeviceSize();

  // Handle opening and closing the popover
  const [isOpen, setIsOpen] = useState(false);
  const togglePopover = () => setIsOpen(!isOpen);

  // Creating a ref for popover
  const popRef = useRef<HTMLDivElement | null>(null);

  const firstTag = tags[0];
  const tagsCount = tags.length;

  return (
    <div className="order-tags w-full">
      <span ref={popRef} className="flex !gap-2 hover:text-primary w-full">
        <span className="flex px-[5px] py-[2px] justify-center items-center rounded-full border border-[var(--shp-color-genericui-lightbluegray)] text-truncate bg-white tag-badge">
          {firstTag && (
            <span className="text-sm truncate interactive">{firstTag}</span>
          )}
        </span>
        {tagsCount > 1 && (
          <span className="text-sm flex px-[7px] py-[2px] justify-center items-center rounded-full border border-[var(--shp-color-genericui-lightbluegray)] text-truncate bg-white interactive tag-badge">
            +{tagsCount - 1}
          </span>
        )}
      </span>
      <UncontrolledPopover
        target={popRef}
        placement={isMobile || isTablet ? 'top' : 'bottom'}
        isOpen={isOpen}
        toggle={togglePopover}
        trigger="hover"
        popperClassName="!max-w-[300px]"
      >
        <PopoverHeader>
          {t(transColumns.tags, { count: tagsCount })}
        </PopoverHeader>
        <PopoverBody
          className="max-h-72 overflow-y-auto shadow-down flex flex-wrap items-center !gap-2"
          data-test="notes-list-wrapper"
        >
          {tags.map(tag => (
            <span
              key={tag}
              className="flex px-[5px] py-[2px] rounded-full border border-[var(--shp-color-genericui-lightbluegray)] text-truncate"
            >
              <span className="text-sm truncate">{tag}</span>
            </span>
          ))}
        </PopoverBody>
      </UncontrolledPopover>
    </div>
  );
};

export default OrderTags;
